"use client";
import { useEffect, useMemo, useState } from "react";

import { useWallet } from "@src/context/WalletProvider";
import { isLeaseStatusWithheld, useProviderCredentials } from "@src/hooks/useProviderCredentials/useProviderCredentials";
import type { LeaseStatusDto } from "@src/queries/useLeaseQuery";
import { useDeploymentLeaseList, useLeaseStatuses } from "@src/queries/useLeaseQuery";
import type { DeploymentDto, LeaseDto, ListedDeploymentDto } from "@src/types/deployment";
import type { ApiProviderList } from "@src/types/provider";
import { isLeaseLive } from "@src/utils/leaseUtils";
import type { WorkloadStatus } from "../DeploymentDetail/DeploymentPlacements/placementModel";
import { isServiceRunning, summarizeWorkloadStatusUnlessWithheld } from "../DeploymentDetail/DeploymentPlacements/placementModel";
import type { VisitEndpoint } from "../DeploymentDetail/DeploymentVisitControl/visitEndpoints";
import { collectVisitEndpoints } from "../DeploymentDetail/DeploymentVisitControl/visitEndpoints";

export const DEPENDENCIES = {
  useWallet,
  useDeploymentLeaseList,
  useLeaseStatuses,
  useProviderCredentials
};

export type UnreachableReason = "not-running" | "starting" | "provider-unreachable" | "no-public-endpoint" | "status-unavailable";

export interface DeploymentReachability {
  leases: LeaseDto[] | null | undefined;
  isLoadingLeases: boolean;
  workload: WorkloadStatus | undefined;
  endpoints: VisitEndpoint[];
  isLoadingEndpoints: boolean;
  unreachableReason: UnreachableReason | null;
}

/**
 * A lease status query stays pending forever when its provider is missing from the provider list or provider
 * credentials never become usable, so the card stops waiting on it and says so instead of spinning.
 */
const ENDPOINT_WAIT_MS = 15_000;

/**
 * Everything a deployment card needs to answer "where do I reach this": its leases, the endpoints its live
 * leases expose, and — when there are none — which reason applies.
 */
export function useDeploymentReachability(params: {
  deployment: Pick<DeploymentDto, "dseq" | "groups" | "state"> & Pick<ListedDeploymentDto, "leases">;
  providers: ApiProviderList[] | undefined;
  dependencies?: typeof DEPENDENCIES;
}): DeploymentReachability {
  const { deployment, providers, dependencies: d = DEPENDENCIES } = params;
  const { address } = d.useWallet();
  /** A list served by the console API answers with each deployment's leases, so only a chain-served one fetches its own. */
  const hasInlineLeases = deployment.leases !== undefined;
  const fetched = d.useDeploymentLeaseList(address, deployment, { enabled: !!address && !hasInlineLeases });
  const leases = deployment.leases ?? fetched.data;
  const isLoadingLeases = !hasInlineLeases && fetched.isLoading;

  const liveLeaseItems = useMemo(
    () =>
      (leases ?? []).filter(isLeaseLive).map(lease => ({
        lease,
        provider: providers?.find(provider => provider.owner === lease.provider)
      })),
    [leases, providers]
  );

  const statuses = d.useLeaseStatuses(liveLeaseItems, { refetchInterval: 30_000 });
  const endpoints = useMemo(() => statuses.flatMap(status => collectVisitEndpoints(status.data)), [statuses]);
  const { tokenMode } = d.useProviderCredentials();
  const isStatusWithheld = isLeaseStatusWithheld(tokenMode, statuses);

  const hasWaitExpired = useHasWaitExpired(ENDPOINT_WAIT_MS, liveLeaseItems.length);
  const isLoadingEndpoints = isLoadingLeases || (!isStatusWithheld && !hasWaitExpired && endpoints.length === 0 && statuses.some(status => status.isPending));

  return {
    leases,
    isLoadingLeases,
    workload: summarizeWorkloadStatusUnlessWithheld(statuses, isStatusWithheld),
    endpoints,
    isLoadingEndpoints,
    unreachableReason: resolveUnreachableReason({ isLoadingEndpoints, endpoints, liveLeaseCount: liveLeaseItems.length, statuses, isStatusWithheld })
  };
}

function resolveUnreachableReason(input: {
  isLoadingEndpoints: boolean;
  endpoints: VisitEndpoint[];
  liveLeaseCount: number;
  statuses: { data?: Pick<LeaseStatusDto, "services"> | null; isPending: boolean }[];
  isStatusWithheld: boolean;
}): UnreachableReason | null {
  if (input.isLoadingEndpoints || input.endpoints.length > 0) return null;
  if (input.liveLeaseCount === 0) return "not-running";
  if (input.isStatusWithheld) return "status-unavailable";
  if (input.statuses.every(status => !status.data)) return "provider-unreachable";
  return input.statuses.some(status => hasStartingService(status.data)) ? "starting" : "no-public-endpoint";
}

function hasStartingService(status: Pick<LeaseStatusDto, "services"> | null | undefined): boolean {
  return Object.values(status?.services ?? {}).some(service => !isServiceRunning(service));
}

/** Restarts on `restartOn` so leases the chain is slow to hand over can't eat the wait before a status request goes out. */
function useHasWaitExpired(waitMs: number, restartOn: unknown): boolean {
  const [hasExpired, setHasExpired] = useState(false);

  useEffect(
    function stopWaitingEventually() {
      setHasExpired(false);
      const timer = setTimeout(() => setHasExpired(true), waitMs);
      return () => clearTimeout(timer);
    },
    [waitMs, restartOn]
  );

  return hasExpired;
}
